// src/pages/MudanzaConfig.js
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    doc,
    onSnapshot,
    serverTimestamp,
    setDoc,
} from 'firebase/firestore';
import {
    FaCalendarAlt,
    FaClock,
    FaHardHat,
    FaInfoCircle,
    FaStickyNote,
    FaTools,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { registerMovement } from '../utils/registerMovement';
import './MudanzaConfig.css';

const DAY_OPTIONS = [
    { key: 'mon', label: 'Lunes' },
    { key: 'tue', label: 'Martes' },
    { key: 'wed', label: 'Miércoles' },
    { key: 'thu', label: 'Jueves' },
    { key: 'fri', label: 'Viernes' },
    { key: 'sat', label: 'Sábado' },
    { key: 'sun', label: 'Domingo' },
];

const DAY_INDEX = DAY_OPTIONS.reduce((acc, option, index) => {
    acc[option.key] = index;
    return acc;
}, {});

const DAY_LABEL = DAY_OPTIONS.reduce((acc, option) => {
    acc[option.key] = option.label;
    return acc;
}, {});

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const DEFAULT_CONFIG = {
    diasPermitidos: ['mon', 'tue', 'wed', 'thu', 'fri'],
    horaInicio: '08:00',
    horaFin: '18:00',
    intervaloMinutos: 60,
    nota: '',
};

const INTERVAL_OPTIONS = [30, 45, 60, 90, 120, 180];

const createDefaultConfig = () => ({
    diasPermitidos: [...DEFAULT_CONFIG.diasPermitidos],
    horaInicio: DEFAULT_CONFIG.horaInicio,
    horaFin: DEFAULT_CONFIG.horaFin,
    intervaloMinutos: DEFAULT_CONFIG.intervaloMinutos,
    nota: DEFAULT_CONFIG.nota,
});

const STRUCTURAL_CATEGORIES = [
    {
        key: 'construccion',
        label: 'Permisos de Construcción',
        helper: 'Define los días y horarios para obras mayores dentro del fraccionamiento.',
        Icon: FaHardHat,
    },
    {
        key: 'remodelacion',
        label: 'Remodelación / Modificación',
        helper: 'Configura la ventana disponible para remodelaciones y modificaciones internas.',
        Icon: FaTools,
    },
];

const createStructuralDefaults = () => STRUCTURAL_CATEGORIES.reduce((acc, category) => {
    acc[category.key] = createDefaultConfig();
    return acc;
}, {});

const sanitizeConfig = (rawConfig) => {
    if (!rawConfig || typeof rawConfig !== 'object') {
        return createDefaultConfig();
    }

    const diasPermitidosRaw = Array.isArray(rawConfig.diasPermitidos)
        ? rawConfig.diasPermitidos.filter((day) => typeof day === 'string' && DAY_INDEX[day] !== undefined)
        : DEFAULT_CONFIG.diasPermitidos;

    const diasPermitidos = [...diasPermitidosRaw];
    diasPermitidos.sort((a, b) => DAY_INDEX[a] - DAY_INDEX[b]);

    const horaInicio = typeof rawConfig.horaInicio === 'string' && TIME_PATTERN.test(rawConfig.horaInicio)
        ? rawConfig.horaInicio
        : DEFAULT_CONFIG.horaInicio;
    const horaFin = typeof rawConfig.horaFin === 'string' && TIME_PATTERN.test(rawConfig.horaFin)
        ? rawConfig.horaFin
        : DEFAULT_CONFIG.horaFin;
    const intervalo = Number.isFinite(Number(rawConfig.intervaloMinutos)) && Number(rawConfig.intervaloMinutos) > 0
        ? Number(rawConfig.intervaloMinutos)
        : DEFAULT_CONFIG.intervaloMinutos;
    const nota = typeof rawConfig.nota === 'string' ? rawConfig.nota : '';

    return {
        diasPermitidos,
        horaInicio,
        horaFin,
        intervaloMinutos: intervalo,
        nota,
    };
};

const sanitizeStructuralConfigs = (rawConfig) => {
    const base = createStructuralDefaults();
    STRUCTURAL_CATEGORIES.forEach((category) => {
        const source = (rawConfig && typeof rawConfig === 'object')
            ? (rawConfig.categorias && rawConfig.categorias[category.key]) || rawConfig[category.key]
            : null;
        base[category.key] = sanitizeConfig(source);
    });
    return base;
};

const buildIntervalOptions = (value) => {
    const numericValue = Number(value);
    if (INTERVAL_OPTIONS.includes(numericValue)) {
        return INTERVAL_OPTIONS;
    }
    const merged = [...INTERVAL_OPTIONS];
    if (Number.isFinite(numericValue) && numericValue > 0) {
        merged.push(numericValue);
    }
    return merged.filter((val, index, array) => array.indexOf(val) === index).sort((a, b) => a - b);
};

const formatDayList = (days) => {
    if (!Array.isArray(days) || days.length === 0) {
        return '';
    }
    return days
        .map((dayKey) => DAY_LABEL[dayKey] || dayKey)
        .join(', ');
};

const MudanzaConfig = () => {
    const { userData, currentUser } = useAuth();
    const { showNotification } = useNotification();

    const normalizedRole = useMemo(
        () => String(userData?.rol || '').toLowerCase(),
        [userData],
    );
    const isSuperAdmin = normalizedRole === 'superadmin';
    const canManage = useMemo(
        () => ['superadmin', 'admin', 'admin_fracc', 'manager', 'platformadmin'].includes(normalizedRole),
        [normalizedRole],
    );
    const fraccionamientoId = useMemo(
        () => userData?.fraccionamientoId || userData?.fraccionamientoTd || null,
        [userData],
    );

    const [config, setConfig] = useState(() => createDefaultConfig());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const dirtyRef = useRef(false);
    useEffect(() => {
        dirtyRef.current = isDirty;
    }, [isDirty]);

    const [structuralConfig, setStructuralConfig] = useState(() => createStructuralDefaults());
    const [structuralLoading, setStructuralLoading] = useState(true);
    const [structuralSaving, setStructuralSaving] = useState(false);
    const [structuralDirty, setStructuralDirty] = useState(false);
    const structuralDirtyRef = useRef(false);
    useEffect(() => {
        structuralDirtyRef.current = structuralDirty;
    }, [structuralDirty]);

    useEffect(() => {
        if (!canManage) {
            setLoading(false);
            return () => {};
        }

        if (!fraccionamientoId) {
            setLoading(false);
            return () => {};
        }

        const configRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'mudanzas');
        const unsubscribe = onSnapshot(configRef, (snapshot) => {
            if (!dirtyRef.current) {
                const next = sanitizeConfig(snapshot.data());
                setConfig(next);
                setIsDirty(false);
            }
            setLoading(false);
        }, (error) => {
            console.error('MudanzaConfig: error al cargar configuración de mudanzas', error);
            setLoading(false);
            showNotification('No se pudo cargar la configuración. Intenta de nuevo más tarde.', 'error');
        });

        return () => unsubscribe();
    }, [canManage, fraccionamientoId, showNotification]);

    useEffect(() => {
        if (!isSuperAdmin) {
            setStructuralLoading(false);
            return () => {};
        }

        if (!fraccionamientoId) {
            setStructuralLoading(false);
            return () => {};
        }

        const structuralRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'permisosEstructurales');
        const unsubscribe = onSnapshot(structuralRef, (snapshot) => {
            if (!structuralDirtyRef.current) {
                const next = sanitizeStructuralConfigs(snapshot.data());
                setStructuralConfig(next);
                setStructuralDirty(false);
            }
            setStructuralLoading(false);
        }, (error) => {
            console.error('MudanzaConfig: error al cargar configuración estructural', error);
            setStructuralLoading(false);
            showNotification('No se pudo cargar la configuración de modificaciones estructurales. Intenta nuevamente más tarde.', 'error');
        });

        return () => unsubscribe();
    }, [fraccionamientoId, isSuperAdmin, showNotification]);

    const updateConfig = useCallback((updater) => {
        let changed = false;
        setConfig((prev) => {
            const partial = typeof updater === 'function' ? updater(prev) : updater;
            if (!partial || typeof partial !== 'object') {
                return prev;
            }
            const next = {
                ...prev,
                ...partial,
            };
            changed = Object.keys(partial).some((key) => next[key] !== prev[key]);
            return changed ? next : prev;
        });
        if (changed) {
            setIsDirty(true);
        }
    }, []);

    const handleToggleDay = useCallback((dayKey) => {
        updateConfig((current) => {
            const days = Array.isArray(current.diasPermitidos) ? [...current.diasPermitidos] : [];
            const index = days.indexOf(dayKey);
            if (index >= 0) {
                days.splice(index, 1);
            } else {
                days.push(dayKey);
                days.sort((a, b) => DAY_INDEX[a] - DAY_INDEX[b]);
            }
            return { diasPermitidos: days };
        });
    }, [updateConfig]);

    const handleInputChange = useCallback((field) => (event) => {
        const value = event.target.value;
        updateConfig({ [field]: value });
    }, [updateConfig]);

    const handleIntervalChange = useCallback((event) => {
        const value = Number(event.target.value) || DEFAULT_CONFIG.intervaloMinutos;
        updateConfig({ intervaloMinutos: value });
    }, [updateConfig]);

    const handleMudanzaReset = useCallback(() => {
        let changed = false;
        const defaults = createDefaultConfig();
        setConfig((prev) => {
            const diff = (
                (prev.diasPermitidos || []).length !== defaults.diasPermitidos.length
                || (prev.diasPermitidos || []).some((day, index) => day !== defaults.diasPermitidos[index])
                || prev.horaInicio !== defaults.horaInicio
                || prev.horaFin !== defaults.horaFin
                || Number(prev.intervaloMinutos) !== Number(defaults.intervaloMinutos)
                || (prev.nota || '') !== (defaults.nota || '')
            );
            changed = diff;
            return diff ? defaults : prev;
        });
        if (changed) {
            setIsDirty(true);
        }
    }, []);

    const updateStructuralConfig = useCallback((categoryKey, updater) => {
        let changed = false;
        setStructuralConfig((prev) => {
            const current = prev[categoryKey] || createDefaultConfig();
            const partial = typeof updater === 'function' ? updater(current) : updater;
            if (!partial || typeof partial !== 'object') {
                return prev;
            }
            const nextCategory = {
                ...current,
                ...partial,
            };
            changed = Object.keys(partial).some((key) => nextCategory[key] !== current[key]);
            if (!changed) {
                return prev;
            }
            return {
                ...prev,
                [categoryKey]: nextCategory,
            };
        });
        if (changed) {
            setStructuralDirty(true);
        }
    }, []);

    const handleStructuralToggleDay = useCallback((categoryKey, dayKey) => {
        updateStructuralConfig(categoryKey, (current) => {
            const days = Array.isArray(current.diasPermitidos) ? [...current.diasPermitidos] : [];
            const index = days.indexOf(dayKey);
            if (index >= 0) {
                days.splice(index, 1);
            } else {
                days.push(dayKey);
                days.sort((a, b) => DAY_INDEX[a] - DAY_INDEX[b]);
            }
            return { diasPermitidos: days };
        });
    }, [updateStructuralConfig]);

    const handleStructuralInputChange = useCallback((categoryKey, field) => (event) => {
        updateStructuralConfig(categoryKey, { [field]: event.target.value });
    }, [updateStructuralConfig]);

    const handleStructuralIntervalChange = useCallback((categoryKey) => (event) => {
        const value = Number(event.target.value) || DEFAULT_CONFIG.intervaloMinutos;
        updateStructuralConfig(categoryKey, { intervaloMinutos: value });
    }, [updateStructuralConfig]);

    const handleStructuralReset = useCallback(() => {
        const defaults = createStructuralDefaults();
        let changed = false;
        setStructuralConfig((prev) => {
            changed = STRUCTURAL_CATEGORIES.some(({ key }) => {
                const current = prev[key] || createDefaultConfig();
                const next = defaults[key];
                if ((current.diasPermitidos || []).length !== next.diasPermitidos.length) {
                    return true;
                }
                if ((current.diasPermitidos || []).some((day, index) => day !== next.diasPermitidos[index])) {
                    return true;
                }
                return (
                    current.horaInicio !== next.horaInicio
                    || current.horaFin !== next.horaFin
                    || Number(current.intervaloMinutos) !== Number(next.intervaloMinutos)
                    || (current.nota || '') !== (next.nota || '')
                );
            });
            return changed ? defaults : prev;
        });
        if (changed) {
            setStructuralDirty(true);
        }
    }, []);

    const validatePayload = useCallback(() => {
        if (!config.diasPermitidos || config.diasPermitidos.length === 0) {
            showNotification('Selecciona al menos un día permitido para los permisos de mudanza.', 'warning');
            return false;
        }

        if (!TIME_PATTERN.test(config.horaInicio) || !TIME_PATTERN.test(config.horaFin)) {
            showNotification('Captura un horario válido en formato HH:MM para los permisos de mudanza.', 'warning');
            return false;
        }

        const startMinutes = Number(config.horaInicio.slice(0, 2)) * 60 + Number(config.horaInicio.slice(3));
        const endMinutes = Number(config.horaFin.slice(0, 2)) * 60 + Number(config.horaFin.slice(3));
        if (endMinutes <= startMinutes) {
            showNotification('La hora de fin debe ser mayor a la hora de inicio en los permisos de mudanza.', 'warning');
            return false;
        }

        if (!Number.isFinite(Number(config.intervaloMinutos)) || Number(config.intervaloMinutos) <= 0) {
            showNotification('Define un intervalo válido en minutos para los permisos de mudanza.', 'warning');
            return false;
        }

        return true;
    }, [config, showNotification]);

    const validateStructuralEntry = useCallback((categoryKey, entry) => {
        const category = STRUCTURAL_CATEGORIES.find((item) => item.key === categoryKey);
        const label = category ? category.label : 'esta categoría';

        if (!entry.diasPermitidos || entry.diasPermitidos.length === 0) {
            showNotification(`Selecciona al menos un día permitido para ${label}.`, 'warning');
            return false;
        }

        if (!TIME_PATTERN.test(entry.horaInicio) || !TIME_PATTERN.test(entry.horaFin)) {
            showNotification(`Captura un horario válido en formato HH:MM para ${label}.`, 'warning');
            return false;
        }

        const startMinutes = Number(entry.horaInicio.slice(0, 2)) * 60 + Number(entry.horaInicio.slice(3));
        const endMinutes = Number(entry.horaFin.slice(0, 2)) * 60 + Number(entry.horaFin.slice(3));
        if (endMinutes <= startMinutes) {
            showNotification(`La hora de fin debe ser mayor a la hora de inicio en ${label}.`, 'warning');
            return false;
        }

        if (!Number.isFinite(Number(entry.intervaloMinutos)) || Number(entry.intervaloMinutos) <= 0) {
            showNotification(`Define un intervalo válido en minutos para ${label}.`, 'warning');
            return false;
        }

        return true;
    }, [showNotification]);

    const handleSave = useCallback(async () => {
        if (!fraccionamientoId) {
            return;
        }
        if (!validatePayload()) {
            return;
        }

        setSaving(true);
        try {
            const payload = {
                diasPermitidos: [...config.diasPermitidos],
                horaInicio: config.horaInicio,
                horaFin: config.horaFin,
                intervaloMinutos: Number(config.intervaloMinutos),
                nota: config.nota || '',
                updatedAt: serverTimestamp(),
            };
            if (currentUser?.uid) {
                payload.updatedBy = currentUser.uid;
            }

            const configRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'mudanzas');
            await setDoc(configRef, payload, { merge: true });

            try {
                const daySummary = formatDayList(payload.diasPermitidos);
                await registerMovement({
                    fraccionamientoId,
                    titulo: 'Actualización de horarios de mudanzas',
                    descripcion: `Días: ${daySummary || 'N/D'} · Horario: ${payload.horaInicio} - ${payload.horaFin} · Intervalo: ${payload.intervaloMinutos} minutos`,
                    categoria: 'Configuraciones',
                    tipoMovimiento: 'config_mudanzas_actualizada',
                    origen: 'configuraciones_mudanza',
                    metadata: {
                        diasPermitidos: payload.diasPermitidos,
                        horaInicio: payload.horaInicio,
                        horaFin: payload.horaFin,
                        intervaloMinutos: payload.intervaloMinutos,
                        nota: payload.nota || null,
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('MudanzaConfig: no se pudo registrar movimiento de mudanzas', logError);
            }

            setIsDirty(false);
            showNotification('Configuración de permisos de mudanza guardada correctamente.', 'success');
        } catch (error) {
            console.error('MudanzaConfig: error al guardar configuración', error);
            showNotification('No se pudo guardar la configuración. Revisa tu conexión e intenta nuevamente.', 'error');
        } finally {
            setSaving(false);
        }
    }, [config, currentUser?.uid, currentUser, fraccionamientoId, showNotification, userData, validatePayload]);

    const handleStructuralSave = useCallback(async () => {
        if (!fraccionamientoId || !isSuperAdmin) {
            return;
        }

        for (let index = 0; index < STRUCTURAL_CATEGORIES.length; index += 1) {
            const { key } = STRUCTURAL_CATEGORIES[index];
            const entry = structuralConfig[key] || createDefaultConfig();
            if (!validateStructuralEntry(key, entry)) {
                return;
            }
        }

        setStructuralSaving(true);
        try {
            const categoriasPayload = STRUCTURAL_CATEGORIES.reduce((acc, { key }) => {
                const entry = structuralConfig[key] || createDefaultConfig();
                acc[key] = {
                    diasPermitidos: [...entry.diasPermitidos],
                    horaInicio: entry.horaInicio,
                    horaFin: entry.horaFin,
                    intervaloMinutos: Number(entry.intervaloMinutos),
                    nota: entry.nota || '',
                };
                return acc;
            }, {});

            const payload = {
                categorias: categoriasPayload,
                updatedAt: serverTimestamp(),
            };

            if (currentUser?.uid) {
                payload.updatedBy = currentUser.uid;
            }

            const structuralRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'permisosEstructurales');
            await setDoc(structuralRef, payload, { merge: true });

            try {
                const summary = STRUCTURAL_CATEGORIES.map(({ key, label }) => {
                    const entry = categoriasPayload[key];
                    const daySummary = formatDayList(entry.diasPermitidos);
                    return `${label}: ${daySummary || 'Sin días'} · ${entry.horaInicio} - ${entry.horaFin}`;
                }).join(' | ');

                await registerMovement({
                    fraccionamientoId,
                    titulo: 'Actualización de horarios de construcción y remodelación',
                    descripcion: summary,
                    categoria: 'Configuraciones',
                    tipoMovimiento: 'config_modificaciones_estructurales_actualizada',
                    origen: 'configuraciones_permisos_estructurales',
                    metadata: {
                        categorias: categoriasPayload,
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('MudanzaConfig: no se pudo registrar movimiento estructural', logError);
            }

            setStructuralDirty(false);
            showNotification('Configuración de permisos de construcción y remodelación guardada correctamente.', 'success');
        } catch (error) {
            console.error('MudanzaConfig: error al guardar configuración estructural', error);
            showNotification('No se pudo guardar la configuración de modificaciones estructurales. Revisa tu conexión e intenta nuevamente.', 'error');
        } finally {
            setStructuralSaving(false);
        }
    }, [currentUser?.uid, currentUser, fraccionamientoId, isSuperAdmin, showNotification, structuralConfig, userData, validateStructuralEntry]);

    const intervalOptions = useMemo(() => buildIntervalOptions(config.intervaloMinutos), [config.intervaloMinutos]);

    if (!canManage) {
        return (
            <div className="mudanza-config-page">
                <header className="mudanza-config-header">
                    <div className="mudanza-config-header-content">
                        <h1>Horarios de mudanza y modificaciones estructurales</h1>
                        <p>No cuentas con permisos para modificar estas configuraciones.</p>
                    </div>
                    <Link to="/configuraciones" className="mudanza-config-back">&lt; Volver a Configuraciones</Link>
                </header>
                <div className="mudanza-config-alert">
                    Solicita acceso de administrador para gestionar los horarios de mudanza y modificaciones.
                </div>
            </div>
        );
    }

    if (!fraccionamientoId) {
        return (
            <div className="mudanza-config-page">
                <header className="mudanza-config-header">
                    <div className="mudanza-config-header-content">
                        <h1>Horarios de mudanza y modificaciones estructurales</h1>
                        <p>No se encontró un fraccionamiento asignado a tu usuario.</p>
                    </div>
                    <Link to="/configuraciones" className="mudanza-config-back">&lt; Volver a Configuraciones</Link>
                </header>
                <div className="mudanza-config-alert">
                    Solicita acceso a un fraccionamiento para administrar los horarios disponibles.
                </div>
            </div>
        );
    }

    return (
        <div className="mudanza-config-page">
            <header className="mudanza-config-header">
                <div className="mudanza-config-header-content">
                    <h1>Horarios de mudanza y modificaciones estructurales</h1>
                    <p>Define los bloques disponibles para permisos de mudanza, construcción y remodelaciones.</p>
                </div>
                <Link to="/configuraciones" className="mudanza-config-back">&lt; Volver a Configuraciones</Link>
            </header>

            {loading ? (
                <div className="mudanza-config-loading">Cargando configuración de mudanzas…</div>
            ) : (
                <div className="mudanza-config-content">
                    <section className="mudanza-config-card">
                        <h2><FaCalendarAlt /> Días permitidos</h2>
                        <p className="mudanza-config-helper">Selecciona los días en los que se permitirá programar mudanzas.</p>
                        <div className="mudanza-day-grid">
                            {DAY_OPTIONS.map((option) => {
                                const isActive = config.diasPermitidos.includes(option.key);
                                return (
                                    <button
                                        key={option.key}
                                        type="button"
                                        className={`mudanza-day-button ${isActive ? 'active' : ''}`}
                                        onClick={() => handleToggleDay(option.key)}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section className="mudanza-config-card">
                        <h2><FaClock /> Horario de atención</h2>
                        <p className="mudanza-config-helper">Establece la ventana de tiempo en la que se pueden agendar entradas o salidas.</p>
                        <div className="mudanza-time-grid">
                            <label htmlFor="mudanza-hora-inicio">
                                Hora de inicio
                                <input
                                    id="mudanza-hora-inicio"
                                    type="time"
                                    value={config.horaInicio}
                                    onChange={handleInputChange('horaInicio')}
                                />
                            </label>
                            <label htmlFor="mudanza-hora-fin">
                                Hora de fin
                                <input
                                    id="mudanza-hora-fin"
                                    type="time"
                                    value={config.horaFin}
                                    onChange={handleInputChange('horaFin')}
                                />
                            </label>
                            <label htmlFor="mudanza-intervalo">
                                Intervalo entre citas
                                <select
                                    id="mudanza-intervalo"
                                    value={config.intervaloMinutos}
                                    onChange={handleIntervalChange}
                                >
                                    {intervalOptions.map((minutes) => (
                                        <option key={minutes} value={minutes}>
                                            {minutes} minutos
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </section>

                    <section className="mudanza-config-card">
                        <h2><FaStickyNote /> Nota informativa</h2>
                        <p className="mudanza-config-helper"><FaInfoCircle /> Esta nota aparecerá como recordatorio al momento de solicitar una mudanza.</p>
                        <textarea
                            value={config.nota}
                            onChange={handleInputChange('nota')}
                            rows={4}
                            placeholder="Ej. Recordar que el acceso para camiones es por la puerta norte."
                        />
                    </section>
                </div>
            )}

            <footer className="mudanza-config-footer">
                <button
                    type="button"
                    className="mudanza-secondary-button"
                    onClick={handleMudanzaReset}
                    disabled={saving || loading}
                >
                    Restaurar predeterminados
                </button>
                <button
                    type="button"
                    className="mudanza-primary-button"
                    onClick={handleSave}
                    disabled={saving || loading}
                >
                    {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
            </footer>

            {isSuperAdmin && (
                <section className="mudanza-structural-section">
                    <div className="mudanza-structural-header">
                        <h2>Configuración de construcción y remodelaciones</h2>
                        <p>Personaliza el calendario disponible para permisos de construcción y trabajos de remodelación.</p>
                    </div>

                    {structuralLoading ? (
                        <div className="mudanza-config-loading">Cargando configuración de modificaciones…</div>
                    ) : (
                        <div className="structural-config-grid">
                            {STRUCTURAL_CATEGORIES.map((category) => {
                                const categoryConfig = structuralConfig[category.key] || createDefaultConfig();
                                const IconComponent = category.Icon;
                                const prefix = `struct-${category.key}`;
                                const intervalChoices = buildIntervalOptions(categoryConfig.intervaloMinutos);

                                return (
                                    <section key={category.key} className="mudanza-config-card structural-card">
                                        <h3>
                                            <IconComponent />
                                            {category.label}
                                        </h3>
                                        <p className="mudanza-config-helper">{category.helper}</p>

                                        <div className="mudanza-config-subsection">
                                            <span className="mudanza-config-subtitle">Días permitidos</span>
                                            <div className="mudanza-day-grid">
                                                {DAY_OPTIONS.map((option) => {
                                                    const isActive = categoryConfig.diasPermitidos.includes(option.key);
                                                    return (
                                                        <button
                                                            type="button"
                                                            className={`mudanza-day-button ${isActive ? 'active' : ''}`}
                                                            onClick={() => handleStructuralToggleDay(category.key, option.key)}
                                                        >
                                                            {option.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="mudanza-config-subsection">
                                            <span className="mudanza-config-subtitle">Ventana de atención</span>
                                            <div className="mudanza-time-grid">
                                                <label htmlFor={`${prefix}-hora-inicio`}>
                                                    Hora de inicio
                                                    <input
                                                        id={`${prefix}-hora-inicio`}
                                                        type="time"
                                                        value={categoryConfig.horaInicio}
                                                        onChange={handleStructuralInputChange(category.key, 'horaInicio')}
                                                    />
                                                </label>
                                                <label htmlFor={`${prefix}-hora-fin`}>
                                                    Hora de fin
                                                    <input
                                                        id={`${prefix}-hora-fin`}
                                                        type="time"
                                                        value={categoryConfig.horaFin}
                                                        onChange={handleStructuralInputChange(category.key, 'horaFin')}
                                                    />
                                                </label>
                                                <label htmlFor={`${prefix}-intervalo`}>
                                                    Intervalo entre citas
                                                    <select
                                                        id={`${prefix}-intervalo`}
                                                        value={categoryConfig.intervaloMinutos}
                                                        onChange={handleStructuralIntervalChange(category.key)}
                                                    >
                                                        {intervalChoices.map((minutes) => (
                                                            <option key={`${prefix}-${minutes}`} value={minutes}>
                                                                {minutes} minutos
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            </div>
                                        </div>

                                        <div className="mudanza-config-subsection">
                                            <span className="mudanza-config-subtitle">Nota informativa</span>
                                            <textarea
                                                id={`${prefix}-nota`}
                                                value={categoryConfig.nota}
                                                onChange={handleStructuralInputChange(category.key, 'nota')}
                                                rows={4}
                                                placeholder="Ej. Las obras deben respetar el horario de silencio del fraccionamiento."
                                            />
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    )}

                    <footer className="mudanza-config-footer">
                        <button
                            type="button"
                            className="mudanza-secondary-button"
                            onClick={handleStructuralReset}
                            disabled={structuralSaving || structuralLoading}
                        >
                            Restaurar predeterminados
                        </button>
                        <button
                            type="button"
                            className="mudanza-primary-button"
                            onClick={handleStructuralSave}
                            disabled={structuralSaving || structuralLoading}
                        >
                            {structuralSaving ? 'Guardando…' : 'Guardar cambios'}
                        </button>
                    </footer>
                </section>
            )}
        </div>
    );
};

export default MudanzaConfig;
